import Vue from 'vue';
import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { moveConversation } from './conversationKanbanMove';
import {
  buildColumnQuery,
  getGroupValue,
  MAX_KANBAN_COLUMNS,
} from 'dashboard/components/widgets/conversation/kanban/helpers';

// Max parallel `/filter` requests per board load. The board fetches one request
// per column; capping concurrency keeps a wide custom-attribute board from
// hammering the API with a burst of expensive filter queries at once.
const FETCH_CONCURRENCY = 4;

// Cards are fetched (and "load more"-d) 50 at a time per column. High-volume
// columns can hold 1000+ conversations, so we page rather than load all.
const KANBAN_PAGE_SIZE = 50;

const emptyColumn = () => ({
  ids: [],
  adopted: [],
  page: 0,
  total: 0,
  hasMore: false,
  loading: false,
});

const state = {
  // Grouping config the columns were fetched for: { type, key }.
  groupBy: { type: 'status' },
  // Snapshot of the board's active global filters (ChatList.conversationFilters).
  baseFilters: {},
  // The user's advanced filter (or the active folder's saved query) as a
  // condition payload. Sent as its own ANDed group so the column condition in
  // `queryData` always holds, even when the user's filter contains an `or`.
  userFilterPayload: [],
  // Per-column pagination metadata keyed by column id. Records themselves live
  // in the canonical `conversations` store so websocket updates keep flowing.
  columns: {},
  // Bumped on every setup. In-flight fetches capture it and drop their result
  // if it changed meanwhile, so a slow response from a previous filter/grouping
  // can never overwrite the columns of a newer one.
  generation: 0,
  // Lane of the conversation open beside the board (null = none). Set only
  // once KanbanView has resolved a rendered column for that conversation, so
  // ConversationView keeps the list beside the ticket only when there is a
  // lane to show; otherwise it falls back to the full-width layout.
  focusedColumnId: null,
};

export const getters = {
  isBoardActive: $state => Object.keys($state.columns).length > 0,
  focusedColumnId: $state => $state.focusedColumnId,
  hasFocusedLane: $state => $state.focusedColumnId !== null,

  // Index of the canonical conversation list, rebuilt only when it changes
  // (Vuex caches this) so hydrating N columns per render stays O(N) lookups
  // instead of rebuilding the Map once per column.
  conversationsById: (_state, _getters, rootState) =>
    new Map(
      (rootState.conversations.allConversations || []).map(chat => [
        chat.id,
        chat,
      ])
    ),

  // Hydrate a column's stored ids against the canonical conversation list.
  getColumn: ($state, $getters) => columnId => {
    const column = $state.columns[columnId] || emptyColumn();
    const byId = $getters.conversationsById;
    const ownIds = new Set(column.ids);
    const native = column.ids
      .map(id => byId.get(id))
      .filter(Boolean)
      // Membership guard: a card whose grouping value changed via a websocket
      // update (e.g. pending -> resolved) leaves its old column immediately
      // instead of lingering with a stale position until the next refetch.
      .filter(chat => getGroupValue(chat, $state.groupBy) === columnId);
    const seen = new Set(ownIds);
    const movedIn = [];
    Object.keys($state.columns).forEach(otherId => {
      if (otherId === columnId) return;
      $state.columns[otherId].ids.forEach(id => {
        if (seen.has(id)) return;
        const chat = byId.get(id);
        if (chat && getGroupValue(chat, $state.groupBy) === columnId) {
          seen.add(id);
          movedIn.push(chat);
        }
      });
    });
    const conversations = [...movedIn, ...native];
    // Totals are the server's, membership is ours. The badge reports the
    // `all_count` of this column's last fetch and nothing else — patching it
    // with locally observed moves is what made it drift, because there is no
    // way to tell from here whether that server count already reflects the
    // move. A stale badge is off by the moves since the last refresh and heals
    // on the next one; a patched badge compounded its error every render.
    return {
      conversations,
      total: column.total,
      hasMore: column.hasMore,
      loading: column.loading,
    };
  },
};

// Ids parked in `columnId` whose grouping value has since moved them
// elsewhere, paired with the column they now belong to.
const collectColumnMoves = ($state, rootState, columnId) => {
  const column = $state.columns?.[columnId];
  if (!column) return [];
  const byId = new Map(
    (rootState?.conversations?.allConversations || []).map(chat => [
      chat.id,
      chat,
    ])
  );
  return column.ids.reduce((moves, id) => {
    const chat = byId.get(id);
    if (!chat) return moves;
    const value = getGroupValue(chat, $state.groupBy);
    if (value !== columnId) moves.push({ id, to: value });
    return moves;
  }, []);
};

const columnLoads = new Map();

const withoutKnownConversations = (conversationList, rootState) => {
  const known = new Set(
    (rootState?.conversations?.allConversations || []).map(chat => chat.id)
  );
  return conversationList.filter(chat => !known.has(chat.id));
};

export { statusForStage } from './conversationKanbanMove';

export const actions = {
  // Reset the board for a new grouping/filter context and fetch page 1 of every
  // column with bounded concurrency.
  setup: async (
    { commit, dispatch },
    { groupBy, columns, baseFilters, userFilterPayload }
  ) => {
    commit(types.SET_KANBAN_CONTEXT, {
      groupBy,
      baseFilters,
      userFilterPayload,
    });

    const limited = columns.slice(0, MAX_KANBAN_COLUMNS);
    if (columns.length > MAX_KANBAN_COLUMNS) {
      // Never truncate coverage silently — the dropped columns must be visible.
      // eslint-disable-next-line no-console
      console.warn(
        `[kanban] ${columns.length} columns requested, fetching first ` +
          `${MAX_KANBAN_COLUMNS}; ${columns.length - MAX_KANBAN_COLUMNS} ` +
          `column(s) were not loaded.`
      );
    }

    commit(
      types.RESET_KANBAN_COLUMNS,
      limited.map(column => column.id)
    );

    const queue = [...limited];
    const worker = async () => {
      const column = queue.shift();
      if (!column) return;
      await dispatch('fetchColumn', { columnId: column.id, page: 1 });
      await worker();
    };
    await Promise.all(
      Array.from({ length: Math.min(FETCH_CONCURRENCY, queue.length) }, worker)
    );
  },

  // Fetch one page of a single column via the advanced filter endpoint and merge
  // the records into the canonical conversation store.
  fetchColumn: async (
    { commit, dispatch, state: $state, rootState, rootGetters },
    { columnId, page, refresh = false }
  ) => {
    // Capture the context this fetch belongs to; if a newer setup bumps the
    // generation while we await, the response is stale and must be dropped.
    const generation = $state.generation;
    const currentUserId = rootGetters.getCurrentUserID;
    // On the "me" tab the column is scoped to assignee_id = current user.
    // Without that id the request would be unscoped and leak other agents'
    // conversations, so skip the fetch until the user is known (a re-setup
    // fires once currentUserId lands — see KanbanView.setupKey).
    if ($state.baseFilters.assigneeType === 'me' && !currentUserId) {
      commit(types.SET_KANBAN_COLUMN_LOADING, { columnId, loading: false });
      return;
    }
    commit(types.SET_KANBAN_COLUMN_LOADING, { columnId, loading: true });
    try {
      const { queryData, andPayload, sortBy } = buildColumnQuery({
        columnId,
        groupBy: $state.groupBy,
        baseFilters: $state.baseFilters,
        userFilterPayload: $state.userFilterPayload,
        currentUserId,
      });
      const { data } = await ConversationApi.filter({
        queryData,
        andPayload,
        page,
        sortBy,
        pageSize: KANBAN_PAGE_SIZE,
        // Board only renders all_count per column; skip the assignee counts.
        leanCount: true,
      });
      if ($state.generation !== generation) return;
      const { payload: conversationList = [], meta = {} } = data;
      const rows = refresh
        ? withoutKnownConversations(conversationList, rootState)
        : conversationList;

      // Reuse the canonical upsert so records stay de-duplicated and reactive to
      // websocket events, exactly like the list view. The `conversations` module
      // is NOT namespaced, so commit the mutation at the root with no prefix.
      if (!refresh || rows.length) {
        commit(types.SET_ALL_CONVERSATION, rows, { root: true });
      }
      // Seed the contacts store with the full senders. Live conversation events
      // ship `meta.sender` without its jsonb blobs (EventDataPresenter
      // #broadcast_meta) and replace `meta` on merge, so the card reads the
      // contact's company from here, where SET_CONTACT_ITEM merges instead of
      // overwriting. Per-item, same as the list's heal path, so the Contacts
      // screen's sortOrder is left alone.
      rows.forEach(chat => {
        const sender = chat.meta?.sender;
        if (sender?.id) {
          commit(`contacts/${types.SET_CONTACT_ITEM}`, sender, { root: true });
        }
      });
      dispatch('conversationLabels/setBulkConversationLabels', rows, {
        root: true,
      });

      // Re-bucket BEFORE the page lands. The response carries a fresh
      // `all_count` that already reflects every status change the server knows
      // about, so any id still parked in this column that no longer belongs
      // has to be handed over now — otherwise `getColumn` would keep
      // subtracting it from a count that already excludes it (and page 1, which
      // replaces the ids outright, would drop the card off the board entirely
      // until its new column refetched).
      commit(types.REBUCKET_KANBAN_COLUMN, {
        columnId,
        moves: collectColumnMoves($state, rootState, columnId),
      });

      commit(types.APPEND_KANBAN_COLUMN_PAGE, {
        columnId,
        page,
        ids: conversationList.map(chat => chat.id),
        total: meta.all_count ?? 0,
        refresh,
      });
    } catch (error) {
      if ($state.generation !== generation) return;
      commit(types.SET_KANBAN_COLUMN_LOADING, { columnId, loading: false });
    }
  },

  setFocusedColumn: ({ commit }, columnId) => {
    commit(types.SET_KANBAN_FOCUSED_COLUMN, columnId);
  },

  loadMoreColumn: async ({ dispatch, state: $state }, { columnId }) => {
    const column = $state.columns[columnId];
    if (!column || column.loading || !column.hasMore) return;
    const load = dispatch('fetchColumn', { columnId, page: column.page + 1 });
    columnLoads.set(columnId, load);
    try {
      await load;
    } finally {
      if (columnLoads.get(columnId) === load) columnLoads.delete(columnId);
    }
  },

  moveConversation,

  refreshColumn: async ({ dispatch, state: $state }, columnId) => {
    await columnLoads.get(columnId)?.catch(() => null);
    const column = $state.columns[columnId];
    if (!column?.page || !column.hasMore) return;
    await dispatch('fetchColumn', {
      columnId,
      page: column.page,
      refresh: true,
    });
  },

  reset: ({ commit }) => commit(types.RESET_KANBAN),
};

export const mutations = {
  [types.SET_KANBAN_CONTEXT](
    $state,
    { groupBy, baseFilters, userFilterPayload }
  ) {
    $state.groupBy = groupBy;
    $state.baseFilters = baseFilters;
    $state.userFilterPayload = userFilterPayload || [];
    // Invalidate any fetch still in flight for the previous context.
    $state.generation += 1;
  },

  [types.RESET_KANBAN_COLUMNS]($state, columnIds) {
    const columns = {};
    columnIds.forEach(id => {
      columns[id] = emptyColumn();
    });
    Vue.set($state, 'columns', columns);
  },

  [types.SET_KANBAN_COLUMN_LOADING]($state, { columnId, loading }) {
    const column = $state.columns[columnId];
    if (column) Vue.set(column, 'loading', loading);
  },

  // Hands cards that changed grouping value over to their new column. Only the
  // ids move — totals stay server-owned (see `getColumn`), so this can run as
  // often as needed without ever inflating a badge.
  [types.REBUCKET_KANBAN_COLUMN]($state, { columnId, moves }) {
    const source = $state.columns[columnId];
    if (!source || !moves.length) return;

    const movedIds = new Set(moves.map(move => move.id));
    source.ids = source.ids.filter(id => !movedIds.has(id));

    moves.forEach(({ id, to }) => {
      const destination = $state.columns[to];
      // A value with no column on the board simply drops off.
      if (!destination || destination.ids.includes(id)) return;
      destination.ids = [id, ...destination.ids];
      Vue.set(destination, 'adopted', [id, ...(destination.adopted || [])]);
    });
  },

  [types.APPEND_KANBAN_COLUMN_PAGE](
    $state,
    { columnId, page, ids, total, refresh = false }
  ) {
    const column = $state.columns[columnId] || emptyColumn();
    const keptAdopted = refresh
      ? (column.adopted || []).filter(
          id => column.ids.includes(id) && !ids.includes(id)
        )
      : [];
    // Page 1 replaces (fresh load); later pages append. De-dup defensively in
    // case an id shifts pages between requests.
    const merged = page <= 1 ? [...keptAdopted] : [...column.ids];
    ids.forEach(id => {
      if (!merged.includes(id)) merged.push(id);
    });
    const loadedPages = refresh ? Math.max(column.page, page) : page;
    Vue.set($state.columns, columnId, {
      ids: merged,
      adopted: page <= 1 ? keptAdopted : column.adopted || [],
      page: loadedPages,
      total,
      hasMore: refresh
        ? loadedPages * KANBAN_PAGE_SIZE < total
        : merged.length < total,
      loading: false,
    });
  },

  [types.SET_KANBAN_FOCUSED_COLUMN]($state, columnId) {
    $state.focusedColumnId = columnId;
  },

  [types.RESET_KANBAN]($state) {
    Vue.set($state, 'columns', {});
    $state.baseFilters = {};
    $state.groupBy = { type: 'status' };
    $state.focusedColumnId = null;
    // Drop any fetch still in flight so it can't re-create a column post-reset.
    $state.generation += 1;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
